import '@theme/dialog';
import { addToCart } from '@theme/fd-cart-add';
import { isClickedOutside } from '@theme/utilities';

/**
 * The Fandom quick add pop-up (snippets/fd-quick-add-modal.liquid, sections/fd-quick-add.liquid).
 *
 * Opening: every product card's "choose" button opens it — Horizon's `button.quick-add__button--choose` (home rows,
 * product recommendations, any Horizon card) and the collection page's `button.fd-col-card__choose`. A click listener
 * on `window` in the capture phase runs before Horizon's declarative `on:click` handlers (on `document`, capture) and
 * before the collection page's own listener, stops the click there and calls `open()`. The theme editor's reopen
 * (assets/theme-editor.js clicks the card's button) takes the same path. Single-variant "+" buttons
 * (`.quick-add__button--add`) are left alone: they still add straight to the cart.
 *
 * `open(productUrl, trigger)`:
 * - fetches `{product path}?section_id=fd-quick-add` (the path keeps the locale prefix, e.g. /ar/products/…), once
 *   per product path for the page's lifetime (a failed fetch is forgotten, so the next try fetches again; so is a
 *   product added to the cart from the pop-up, so a reopen shows its stock as it is then);
 * - marks the trigger `aria-busy="true"` while it loads; a failed load opens the product page instead;
 * - puts the section's `.fd-qa` root into `#quick-add-modal-content` (a fresh copy each time, so prices are new
 *   span.money nodes that assets/fd-currency.js converts), with `data-requested-variant` when the URL had ?variant=;
 * - shows the dialog through Horizon's dialog-component (assets/dialog.js: scroll lock, Esc, a click outside).
 * The latest call wins: an earlier load that finishes later is dropped.
 * A card button passes on ?variant= only for the colour of a swatch checked on the card (Horizon's card links always
 * carry the first variant in stock, which would choose a size for the shopper).
 *
 * Closing: dialog.js closes on ×, Esc (as a keydown inside the component) and a click outside; a click outside in the
 * first 300 ms after opening is ignored (the second click of a double-click on the card button). The browser's own close
 * request (Esc with the focus outside the component, Android's back gesture) goes through closeDialog() when it can be
 * cancelled; a close that skipped closeDialog() still unlocks the page. After every close the focus goes back to the
 * button that opened the pop-up (Horizon's card keeps that button visible while the pop-up is open: `stay-visible`).
 *
 * `<fd-quick-add>` (FdQuickAdd, the section root) runs the content: the JSON in `script[data-fd-qa-data]` (`data`),
 * and the gallery: `showImage(index)` shows one image (wrapping around), marks its thumbnail `aria-current="true"` and
 * keeps four thumbnails showing around it; ‹ › step by `data-dir`, a thumbnail jumps to its `data-index`.
 *
 * Choosing (all in the browser, from the JSON; nothing is fetched per choice):
 * - `selected` holds the value chosen per option (null until chosen); options with one value are fixed. At the start
 *   the colour is the requested variant's (the card's checked swatch), else the first in stock; nothing else is chosen,
 *   unless only one variant is in stock (of that colour), which is then chosen whole. A colour choice shows that
 *   colour's first variant image.
 * - Every option button gets `data-out` when nothing matching it and the other choices is in stock (its hidden
 *   `.fd-qa__out` then says "Sold out", so it is part of the option's name; a sold-out colour is dimmed and struck
 *   through), and "N left" when that one variant has 1–3 left (`low`).
 * - The button (`data-state`): "added" ("✓ Added to cart", 2.2 s), "out" ("Size sold out", aria-disabled), "choose"
 *   ("Select a size"; pressing it shows "Please select a size" under the options and red borders, `data-error` on the
 *   group) or "ready" ("Add to cart · price × quantity"). A sold-out choice shows "Notify me when {size} is back",
 *   linking the product page on that variant (its back-in-stock form); "View full details" follows the chosen variant.
 *   When no option is a size (only colours, or other options; a size is an option whose name holds "size" or "مقاس"),
 *   the wordings don't say "size": "Select an option", "Please choose {option}", "Sold out", "Notify me when {value} is
 *   back".
 * - The stepper follows the variant's quantity rule (`quantity_rule`: min, increment, max): it steps by the increment
 *   and a typed quantity is brought into the rule.
 * - Prices are new `span.money` nodes in the shop's money format each time they change (never a rewrite of a
 *   converted one), so assets/fd-currency.js has Nova convert them.
 * Adding goes through assets/fd-cart-add.js (shared with the collection card): the cart add route with the section of
 * every cart-items-component; a refusal shows Shopify's description in `.fd-qa__refusal` under the button; only when no
 * answer comes back does the product page open. On success the button reads "✓ Added to cart" for 2.2 s and the pop-up
 * closes; the theme's CartAddEvent goes out once the pop-up has closed (however it closed), so the drawer opens on a page
 * that is no longer locked by the pop-up, with the header count. An add that comes back after the pop-up was closed or
 * replaced goes out at once.
 * The arrival date (`.fd-qa__eta`) is the cart page's (`arrivalDate` in @theme/pdp-delivery, loaded on demand).
 * The size guide (the product page's <pdp-size-guide>, assets/pdp-variants.js) is a dialog inside the pop-up's dialog:
 * its clicks and its Esc are kept from Horizon's dialog-component, which would otherwise close the pop-up too.
 */

const SECTION_ID = 'fd-quick-add';
const TRIGGERS = 'button.quick-add__button--choose, button.fd-col-card__choose';
const THUMBS_SHOWN = 4;
const ADDED_FOR = 2200;
/** A click outside this soon after opening is the second click of a double-click on the card button, not a close. */
const OPEN_GUARD = 300;

/**
 * An amount for the shop's money format placeholder (`{{amount}}` and its variants).
 * @param {number} cents
 * @param {string} key - The placeholder's name
 * @returns {string}
 */
function formatAmount(cents, key) {
  const value = Number(cents) / 100;
  /** @param {number} decimals @param {string} [thousands] @param {string} [decimal] */
  const number = (decimals, thousands = ',', decimal = '.') => {
    const [whole, fraction] = value.toFixed(decimals).split('.');
    const grouped = (whole ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  switch (key) {
    case 'amount_no_decimals':
      return number(0);
    case 'amount_with_comma_separator':
      return number(2, '.', ',');
    case 'amount_no_decimals_with_comma_separator':
      return number(0, '.');
    case 'amount_with_apostrophe_separator':
      return number(2, "'");
    case 'amount_with_space_separator':
      return number(2, ' ', ',');
    case 'amount_no_decimals_with_space_separator':
      return number(0, ' ');
    default:
      return number(2);
  }
}

/**
 * Theme-owned markup (the money format, a locale string) as nodes.
 * @param {string} html
 * @returns {DocumentFragment}
 */
function fragment(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content;
}

/** @type {Map<string, Promise<string>>} Section HTML per product path. */
const cache = new Map();

/** @type {HTMLElement | null} The button that opened the pop-up (focus goes back to it). */
let opener = null;

/** The latest open() call. */
let latest = 0;

/** When the dialog last opened (performance.now()). */
let openedAt = Number.NEGATIVE_INFINITY;

/** @returns {(HTMLElement & { showDialog?: () => void, closeDialog?: () => void }) | null} */
const shell = () => {
  const element = document.getElementById('quick-add-dialog');
  return element?.classList.contains('fd-qa-modal') ? element : null;
};

/**
 * The section's HTML for a product path, fetched once.
 * @param {string} path
 * @returns {Promise<string>}
 */
function load(path) {
  let pending = cache.get(path);
  if (!pending) {
    pending = fetch(`${path}?section_id=${SECTION_ID}`, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    });
    cache.set(path, pending);
    pending.catch(() => cache.delete(path));
  }
  return pending;
}

/**
 * Forgets a product's loaded content (every path that names its handle), so the next open shows its stock as it is now.
 * @param {string} productUrl
 */
function forget(productUrl) {
  const handle = productUrl.split('?')[0].split('/products/')[1];
  if (!handle) return;
  for (const path of cache.keys()) {
    if (path.split('/products/')[1] === handle) cache.delete(path);
  }
}

/**
 * The variant of the swatch checked on a card (Horizon's product-card), if any.
 * @param {Element} button
 * @returns {string | null}
 */
function swatchVariantOf(button) {
  const card = /** @type {any} */ (button.closest('product-card'));
  if (!card) return null;
  try {
    if (typeof card.getSelectedVariantId === 'function') return card.getSelectedVariantId() || null;
  } catch (error) {
    // Read the swatch below.
  }
  const input = card.querySelector('input[type="radio"]:checked[data-variant-id]');
  return input instanceof HTMLInputElement ? input.dataset.variantId || null : null;
}

/**
 * The product page a card button stands for: Horizon's quick-add-component knows it (the card link); the collection
 * card's button carries it too. Horizon's card links always carry ?variant= (the first variant in stock, or the
 * swatch's), so it is set again from the card: only a checked swatch passes its variant on (for its colour), and
 * nothing else does, so the pop-up never opens with a size the shopper did not choose.
 * @param {Element} button
 * @returns {string}
 */
function productUrlOf(button) {
  let url = '';
  const component = /** @type {any} */ (button.closest('quick-add-component'));
  try {
    url = component?.productPageUrl || '';
  } catch (error) {
    // The card is not upgraded yet: fall back to its link.
  }
  if (!url && button instanceof HTMLElement) url = button.dataset.productUrl || '';
  if (!url) {
    const link = button.closest('product-card')?.querySelector('a[href*="/products/"]');
    url = link instanceof HTMLAnchorElement ? link.href : '';
  }
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.href);
    const swatch = swatchVariantOf(button);
    if (swatch) parsed.searchParams.set('variant', swatch);
    else parsed.searchParams.delete('variant');
    return parsed.href;
  } catch (error) {
    return url;
  }
}

/**
 * Opens the quick add pop-up for a product.
 * @param {string} productUrl - The product page URL (locale-aware path; ?variant= is passed on to the content).
 * @param {HTMLElement | null} [trigger] - The button that opens it: busy while it loads, focused again on close.
 */
export async function open(productUrl, trigger = null) {
  let url;
  try {
    url = new URL(productUrl, window.location.href);
  } catch (error) {
    return;
  }
  const component = shell();
  const container = document.getElementById('quick-add-modal-content');
  if (!component || !container || typeof component.showDialog !== 'function') {
    window.location.assign(url.href);
    return;
  }
  if (trigger?.getAttribute('aria-busy') === 'true') return;

  const id = ++latest;
  trigger?.setAttribute('aria-busy', 'true');
  try {
    const html = await load(url.pathname);
    if (id !== latest) return;
    const root = new DOMParser().parseFromString(html, 'text/html').querySelector('.fd-qa');
    if (!root) throw new Error('the section has no .fd-qa');
    const variant = url.searchParams.get('variant');
    if (variant) root.setAttribute('data-requested-variant', variant);
    container.replaceChildren(document.importNode(root, true));

    opener = trigger;
    trigger?.closest('quick-add-component')?.toggleAttribute('stay-visible', true);
    component.showDialog();
  } catch (error) {
    if (id !== latest) return;
    console.error('fd-quick-add: could not load the product', error);
    window.location.assign(url.href);
  } finally {
    trigger?.removeAttribute('aria-busy');
  }
}

/** After any close: unlock the page if closeDialog() did not, and give the focus back to the opening button. */
function onClose() {
  const dialog = shell()?.querySelector('dialog');
  if (dialog?.open) return;
  const { style } = document.body;
  if (style.position === 'fixed') {
    const top = -Number.parseFloat(style.top) || 0;
    style.width = '';
    style.position = '';
    style.top = '';
    window.scrollTo({ top, behavior: 'instant' });
  }
  const trigger = opener;
  opener = null;
  if (trigger?.isConnected) trigger.focus({ preventScroll: true });
  trigger?.closest('quick-add-component')?.toggleAttribute('stay-visible', false);
}

/**
 * The browser's own close request: closed the way dialog.js does (animation, page unlocked) when it can be cancelled.
 * @param {Event} event
 */
function onCancel(event) {
  const component = shell();
  if (typeof component?.closeDialog !== 'function') return;
  event.preventDefault();
  const dialog = /** @type {HTMLDialogElement} */ (event.currentTarget);
  if (!dialog.classList.contains('dialog-closing')) component.closeDialog();
}

/** @param {MouseEvent} event */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(TRIGGERS);
  if (!(button instanceof HTMLButtonElement) || !shell()) return;
  const url = productUrlOf(button);
  if (!url) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  open(url, button);
}

/**
 * Warms the cache when a mouse comes over a "choose" button.
 * @param {PointerEvent} event
 */
function onPointerOver(event) {
  if (event.pointerType !== 'mouse' || !shell()) return;
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(TRIGGERS);
  if (!button) return;
  const url = productUrlOf(button);
  if (!url) return;
  try {
    load(new URL(url, window.location.href).pathname).catch(() => {});
  } catch (error) {
    // An unusable URL: the click will open the product page.
  }
}

/**
 * A click outside the dialog just after it opened is kept from dialog.js (which would close it): the second click of a
 * double-click on the card button lands on the backdrop once the pop-up shows.
 * @param {MouseEvent} event
 */
function onEarlyClick(event) {
  if (performance.now() - openedAt >= OPEN_GUARD) return;
  const dialog = shell()?.querySelector('dialog');
  if (dialog?.open && isClickedOutside(event, dialog)) event.stopPropagation();
}

if (!(/** @type {any} */ (window).fdQuickAdd)) {
  /** @type {any} */ (window).fdQuickAdd = { open };
  window.addEventListener('click', onClick, { capture: true });
  window.addEventListener('pointerover', onPointerOver, { capture: true, passive: true });
  const component = shell();
  component?.addEventListener('dialog:open', () => {
    openedAt = performance.now();
  });
  component?.addEventListener('click', onEarlyClick, { capture: true });
  const dialog = component?.querySelector('dialog');
  dialog?.addEventListener('cancel', onCancel);
  dialog?.addEventListener('close', onClose);
}

/**
 * The pop-up's content for one product (the section root, `.fd-qa`).
 */
export class FdQuickAdd extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {any} The section's JSON: { product, money_format, strings, variants }. */
  data = null;

  /** The image showing in the gallery. */
  index = 0;

  /** The first thumbnail showing. */
  #thumbStart = 0;

  /** @type {(string | null)[]} The value chosen per option, in option order (null: not chosen yet). */
  selected = [];

  /** "Add" was pressed with an option still to choose. */
  #tried = false;

  /** "✓ Added to cart" is showing. */
  #added = false;

  #addedTimer = 0;

  #busy = false;

  /** The cart's refusal, shown in `.fd-qa__refusal` (under the button) until the next choice. */
  #message = '';

  /** @type {Event | null} The CartAddEvent of the last add, sent once the pop-up has closed. */
  #pending = null;

  /** What the price and the button show now, so unchanged prices keep their (converted) nodes. */
  #shownPrice = '';

  #shownLabel = '';

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.data = this.#readData();
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.closest('dialog')?.addEventListener('close', this.#flush, { signal });
    this.showImage(this.index);
    this.#start();
    this.#showEta();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    window.clearTimeout(this.#addedTimer);
    this.#flush();
  }

  /** The variant id the card asked for (?variant= of its checked swatch), if any: only its colour is chosen from it. */
  get requestedVariantId() {
    return this.dataset.requestedVariant || null;
  }

  /** @returns {HTMLImageElement[]} */
  get images() {
    return /** @type {HTMLImageElement[]} */ ([...this.querySelectorAll('.fd-qa__tile img.fd-qa__img[data-index]')]);
  }

  /** @returns {HTMLButtonElement[]} */
  get thumbs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('.fd-qa__thumbs button[data-index]')]);
  }

  /** @returns {HTMLFieldSetElement[]} One per option with two or more values. */
  get groups() {
    return /** @type {HTMLFieldSetElement[]} */ ([...this.querySelectorAll('fieldset.fd-qa__group[data-option-index]')]);
  }

  /** @returns {any[]} */
  get variants() {
    return Array.isArray(this.data?.variants) ? this.data.variants : [];
  }

  /** The chosen quantity, within the variant's quantity rule. */
  get quantity() {
    const input = this.querySelector('input.fd-qa__qty');
    return this.#fitQuantity(input instanceof HTMLInputElement ? input.value : 1);
  }

  /**
   * The quantity rule of the variant the stepper is for (the chosen one, else the one whose price shows).
   * @returns {{ min: number, increment: number, max: number | null }}
   */
  get #rule() {
    const rule = this.#current()?.quantity_rule || {};
    const increment = Math.max(1, Math.floor(Number(rule.increment)) || 1);
    const min = Math.max(1, Math.floor(Number(rule.min)) || 1);
    const max = Number(rule.max) >= min ? Math.floor(Number(rule.max)) : null;
    return { min, increment, max };
  }

  /**
   * A quantity brought into the rule: at least the minimum, a whole number of increments above it, at most the maximum.
   * @param {unknown} value
   * @returns {number}
   */
  #fitQuantity(value) {
    const { min, increment, max } = this.#rule;
    let quantity = Math.floor(Number(value));
    if (!(quantity >= min)) quantity = min;
    quantity = min + Math.round((quantity - min) / increment) * increment;
    if (max !== null && quantity > max) quantity = max - ((max - min) % increment);
    return quantity;
  }

  /** Puts the rule on the quantity field and brings its value into it (after a choice changes the variant). */
  #syncQuantity() {
    const input = this.querySelector('input.fd-qa__qty');
    if (!(input instanceof HTMLInputElement)) return;
    const { min, increment, max } = this.#rule;
    input.min = String(min);
    input.step = String(increment);
    if (max === null) input.removeAttribute('max');
    else input.max = String(max);
    const quantity = String(this.#fitQuantity(input.value));
    if (input.value !== quantity) input.value = quantity;
  }

  /** @returns {any | null} The chosen variant, else the first in stock that fits the choices (whose price shows). */
  #current() {
    const variants = this.variants;
    return (
      this.variant ||
      variants.find((v) => v.available && this.#fits(v)) ||
      variants.find((v) => this.#fits(v)) ||
      variants[0] ||
      null
    );
  }

  /** @returns {any | null} The variant of the current choices, once every option is chosen (null if none matches). */
  get variant() {
    if (this.selected.some((value) => value === null)) return null;
    return this.variants.find((v) => v.options.every((/** @type {string} */ value, /** @type {number} */ i) => value === this.selected[i])) ?? null;
  }

  /** @returns {'added' | 'out' | 'choose' | 'ready'} The main button's state. */
  get state() {
    if (this.#added) return 'added';
    if (this.selected.some((value) => value === null)) return 'choose';
    return this.variant?.available ? 'ready' : 'out';
  }

  /**
   * Shows one image in the tile (wrapping around) and keeps its thumbnail marked and in view.
   * @param {number} index
   */
  showImage(index) {
    const images = this.images;
    const count = images.length;
    if (!count) return;
    const current = ((Math.trunc(index) % count) + count) % count;
    this.index = current;

    for (const image of images) {
      const position = Number(image.dataset.index);
      image.hidden = position !== current;
      // Load the neighbours ahead of a step.
      if (Math.abs(position - current) === 1 || Math.abs(position - current) === count - 1) image.loading = 'eager';
    }

    const thumbs = this.thumbs;
    if (thumbs.length > THUMBS_SHOWN) {
      if (current < this.#thumbStart) this.#thumbStart = current;
      else if (current >= this.#thumbStart + THUMBS_SHOWN) this.#thumbStart = current - THUMBS_SHOWN + 1;
      this.#thumbStart = Math.max(0, Math.min(this.#thumbStart, thumbs.length - THUMBS_SHOWN));
    } else {
      this.#thumbStart = 0;
    }
    for (const thumb of thumbs) {
      const position = Number(thumb.dataset.index);
      thumb.setAttribute('aria-current', String(position === current));
      thumb.hidden = position < this.#thumbStart || position >= this.#thumbStart + THUMBS_SHOWN;
    }
  }

  /** @returns {any} */
  #readData() {
    const script = this.querySelector('script[data-fd-qa-data]');
    try {
      return JSON.parse(script?.textContent || 'null');
    } catch (error) {
      console.error('fd-quick-add: unreadable product data', error);
      return null;
    }
  }

  /**
   * The first choices: the colour of the requested variant (the card's checked swatch), else of the first in stock;
   * never a size or another option, unless only one variant is in stock (of that colour): it is then chosen whole.
   */
  #start() {
    const variants = this.variants;
    if (!variants.length) return;
    const groups = this.groups;
    const grouped = (/** @type {number} */ i) => groups.some((group) => Number(group.dataset.optionIndex) === i);
    this.selected = variants[0].options.map((/** @type {string} */ value, /** @type {number} */ i) => (grouped(i) ? null : value));

    const available = variants.filter((v) => v.available);
    const requested = variants.find((v) => String(v.id) === this.requestedVariantId);
    const base = requested || available[0] || variants[0];
    const colours = groups.filter((group) => group.dataset.kind === 'color').map((group) => Number(group.dataset.optionIndex));
    const only = available.length === 1 ? available[0] : null;
    const whole = only && colours.every((i) => only.options[i] === base.options[i]) ? only : null;
    for (const group of groups) {
      const i = Number(group.dataset.optionIndex);
      if (whole) this.selected[i] = whole.options[i] ?? null;
      else if (group.dataset.kind === 'color') this.selected[i] = base.options[i] ?? null;
      for (const input of group.querySelectorAll('input.fd-qa__radio')) {
        if (input instanceof HTMLInputElement) input.checked = input.value === this.selected[i];
      }
    }

    // The section printed the price and the button for the first variant in stock (a printed "choose" wording is
    // written again: it holds no price to keep).
    const printed = available[0] || variants[0];
    this.#shownPrice = this.#priceKey(printed);
    const button = this.querySelector('button.fd-qa__add');
    this.#shownLabel = button instanceof HTMLElement && button.dataset.state === 'ready' ? `ready:${printed.price}` : '';

    const image = whole?.image_index ?? this.#colourImage();
    if (image !== null && image !== undefined) this.showImage(image);
    this.#syncQuantity();
    this.#render();
  }

  /** @returns {number | null} The chosen colour's first variant image, if the product has colours. */
  #colourImage() {
    const colour = this.groups.find((group) => group.dataset.kind === 'color');
    if (!colour) return null;
    const i = Number(colour.dataset.optionIndex);
    const match = this.variants.find((v) => v.options[i] === this.selected[i] && v.image_index !== null && v.image_index !== undefined);
    return match ? match.image_index : null;
  }

  /**
   * Whether a variant fits the current choices (an option not chosen yet fits anything).
   * @param {any} variant
   * @param {number} [except] - An option index to leave out
   */
  #fits(variant, except = -1) {
    return variant.options.every(
      (/** @type {string} */ value, /** @type {number} */ i) => i === except || this.selected[i] === null || value === this.selected[i]
    );
  }

  /** @param {any} variant */
  #priceKey(variant) {
    return `${variant.price}|${variant.compare_at_price ?? ''}`;
  }

  /**
   * A price in the shop's money format, as a new span.money.
   * @param {number} cents
   * @returns {Node}
   */
  #money(cents) {
    const format = String(this.data?.money_format || '{{amount}}');
    const nodes = fragment(format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => formatAmount(cents, key)));
    if (nodes.querySelector('.money')) return nodes;
    const span = document.createElement('span');
    span.className = 'money';
    span.append(nodes);
    return span;
  }

  /**
   * A locale string with `placeholder` standing for `node`.
   * @param {string} template
   * @param {string} placeholder
   * @param {Node} node
   * @returns {Node[]}
   */
  #fill(template, placeholder, node) {
    const [before = '', after = ''] = String(template).split(placeholder);
    return [fragment(before), node, fragment(after)];
  }

  /** @param {string | number} id */
  #variantUrl(id) {
    const url = new URL(this.data?.product?.url || this.dataset.productUrl || '/', window.location.origin);
    url.searchParams.set('variant', String(id));
    return `${url.pathname}${url.search}`;
  }

  /** Brings every part of the content in line with the current choices. */
  #render() {
    const variants = this.variants;
    if (!variants.length) return;
    const strings = this.data?.strings || {};
    const variant = this.variant;
    const state = this.state;
    const missing = this.selected.some((value) => value === null);

    for (const group of this.groups) {
      const i = Number(group.dataset.optionIndex);
      const value = this.selected[i];
      const label = group.querySelector('.fd-qa__value-label');
      if (label && value !== null) {
        if (label.textContent !== value) label.textContent = value;
        label.classList.remove('fd-qa__value-label--empty');
      }
      group.toggleAttribute('data-error', this.#tried && value === null);

      for (const option of group.querySelectorAll('label.fd-qa__size, label.fd-qa__swatch')) {
        const input = option.querySelector('input.fd-qa__radio');
        if (!(input instanceof HTMLInputElement)) continue;
        const matches = variants.filter((v) => v.options[i] === input.value && this.#fits(v, i));
        const out = !matches.some((v) => v.available);
        option.toggleAttribute('data-out', out);
        // Part of the option's name, so a screen reader says it is sold out.
        const note = option.querySelector('.fd-qa__out');
        const said = out ? String(strings.sold_out || '') : '';
        if (note && note.textContent !== said) note.textContent = said;
        const left = option.querySelector('.fd-qa__left');
        if (left instanceof HTMLElement) {
          const low = matches.length === 1 && matches[0].available ? Number(matches[0].low) || 0 : 0;
          left.hidden = low <= 0;
          const text = low > 0 ? String(strings.left || '[quantity]').replace('[quantity]', String(low)) : '';
          if (left.textContent !== text) left.textContent = text;
        }
      }
    }

    // Price (and the tabby quarter) of the chosen variant, or of the first one in stock that fits the choices.
    const shown = this.#current();
    const priceKey = this.#priceKey(shown);
    if (priceKey !== this.#shownPrice) {
      this.#shownPrice = priceKey;
      const sale = shown.compare_at_price !== null && shown.compare_at_price > shown.price;
      for (const price of this.querySelectorAll('.fd-qa__price')) {
        price.classList.toggle('fd-qa__price--sale', sale);
        /** @type {(Node | string)[]} */
        const nodes = [this.#money(shown.price)];
        if (sale) {
          const was = document.createElement('s');
          was.className = 'fd-qa__was';
          was.append(this.#money(shown.compare_at_price));
          nodes.push(' ', was);
        }
        price.replaceChildren(...nodes);
      }
      for (const quarter of this.querySelectorAll('.fd-qa__quarter')) {
        quarter.replaceChildren(this.#money(Math.round(shown.price / 4)));
      }
    }

    // "Please select a size" under the options; the cart's refusal under the button.
    const error = this.querySelector('.fd-qa__error');
    const please = this.#tried && missing ? this.#pleaseText() : '';
    if (error && error.textContent !== please) error.textContent = please;
    const refusal = this.querySelector('.fd-qa__refusal');
    if (refusal && refusal.textContent !== this.#message) refusal.textContent = this.#message;

    const notify = this.querySelector('a.fd-qa__notify');
    if (notify instanceof HTMLAnchorElement) {
      const show = state === 'out' && !!variant;
      notify.hidden = !show;
      if (show) {
        notify.href = this.#variantUrl(variant.id);
        // "…when size M is back" (the Arabic names the size) only when a size is involved; else the neutral wording.
        const template = this.#hasSize ? strings.notify : strings.notify_any || strings.notify;
        notify.textContent = String(template || '[size]').replace(/\[(?:size|value)\]/, this.#sizeName(variant));
      }
    }

    for (const link of this.querySelectorAll('a.fd-qa__full')) {
      if (link instanceof HTMLAnchorElement) {
        link.href = variant ? this.#variantUrl(variant.id) : this.data?.product?.url || this.dataset.productUrl || link.href;
      }
    }

    const button = this.querySelector('button.fd-qa__add');
    const label = button?.querySelector('.fd-qa__add-label');
    if (button instanceof HTMLElement) {
      button.dataset.state = state;
      if (state === 'out') button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }
    const total = state === 'ready' && variant ? variant.price * this.quantity : 0;
    const key = state === 'ready' ? `ready:${total}` : state;
    if (label && key !== this.#shownLabel) {
      this.#shownLabel = key;
      if (state === 'ready') {
        label.replaceChildren(...this.#fill(strings.add_total || '[total]', '[total]', this.#money(total)));
      } else if (state === 'added') {
        const check = document.createElement('span');
        check.className = 'fd-qa__check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        label.replaceChildren(check, ` ${strings.added || ''}`);
      } else if (state === 'out') {
        label.textContent = String((this.#hasSize ? strings.size_sold_out : strings.sold_out || strings.size_sold_out) || '');
      } else {
        label.textContent = String((this.#hasSize ? strings.select_size : strings.select_option || strings.select_size) || '');
      }
    }
  }

  /** Whether one of the options to choose is a size (its wordings then say "size"). */
  get #hasSize() {
    return this.groups.some((group) => group.dataset.kind === 'size');
  }

  /** "Please select a size", or with no size to choose "Please choose {option}" for the first option still to choose. */
  #pleaseText() {
    const strings = this.data?.strings || {};
    if (this.#hasSize || !strings.please_choose) return String(strings.please_select || '');
    const group = this.groups.find((g) => this.selected[Number(g.dataset.optionIndex)] === null);
    return String(strings.please_choose).replace('[option]', group?.dataset.optionName || '');
  }

  /**
   * The words a "Notify me when … is back" names: the variant's non-colour values (or all of them).
   * @param {any} variant
   */
  #sizeName(variant) {
    const groups = this.groups;
    const sized = groups.filter((group) => group.dataset.kind !== 'color');
    return (sized.length ? sized : groups)
      .map((group) => variant.options[Number(group.dataset.optionIndex)])
      .filter(Boolean)
      .join(' / ');
  }

  /** An option was chosen: the "Added" state ends (the pop-up then stays open) and so does a refusal. */
  #changed() {
    if (this.#added) {
      this.#added = false;
      window.clearTimeout(this.#addedTimer);
    }
    this.#message = '';
  }

  /** @param {number} step */
  #step(step) {
    const input = this.querySelector('input.fd-qa__qty');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = String(this.#fitQuantity(this.quantity + step * this.#rule.increment));
    this.#message = '';
    this.#render();
  }

  /** Sends the add that happened, once the pop-up has closed (or the content has gone). */
  #flush = () => {
    const event = this.#pending;
    this.#pending = null;
    if (event) document.dispatchEvent(event);
  };

  async #add() {
    const state = this.state;
    if (state === 'choose') {
      this.#tried = true;
      this.#message = '';
      this.#render();
      // Put the message in again so a second press is announced too.
      const error = this.querySelector('.fd-qa__error');
      if (error) {
        const text = error.textContent;
        error.textContent = '';
        requestAnimationFrame(() => {
          if (!error.textContent) error.textContent = text;
        });
      }
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.groups
        .find((group) => this.selected[Number(group.dataset.optionIndex)] === null)
        ?.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
      return;
    }

    const variant = this.variant;
    const button = this.querySelector('button.fd-qa__add');
    if (state !== 'ready' || !variant || this.#busy || !(button instanceof HTMLElement)) return;
    const quantity = this.quantity;
    const strings = this.data?.strings || {};

    this.#busy = true;
    this.#message = '';
    this.#render();
    button.setAttribute('aria-busy', 'true');
    /** @type {Awaited<ReturnType<typeof addToCart>>} */
    let added;
    try {
      added = await addToCart({
        id: variant.id,
        quantity,
        source: 'fd-quick-add',
        productId: String(this.data?.product?.id ?? this.dataset.productId ?? ''),
      });
    } catch (error) {
      // No answer at all (offline, blocked): the product page, where the shopper can try again.
      console.error(`fd-quick-add: the cart did not answer for variant ${variant.id}`, error);
      this.#busy = false;
      button.removeAttribute('aria-busy');
      window.location.assign(this.#variantUrl(variant.id));
      return;
    }

    try {
      if (!added.ok || !added.event) {
        const text = added.message || strings.add_error || '';
        console.error(`fd-quick-add: the cart refused variant ${variant.id} (HTTP ${added.status}): ${text}`);
        this.#message = String(text);
        this.#render();
        return;
      }

      // The stock changed: the next open loads the product again.
      forget(String(this.data?.product?.url || this.dataset.productUrl || ''));

      // The pop-up was closed (or another product replaced it) while the cart answered: no close is left to wait for.
      if (!this.isConnected || !this.closest('dialog')?.open) {
        document.dispatchEvent(added.event);
        return;
      }

      this.#pending = added.event;
      this.#added = true;
      this.#render();
      // Emptied first, then filled, so a second "Added" is announced too.
      const live = this.querySelector('.fd-qa__live');
      if (live) {
        live.textContent = '';
        requestAnimationFrame(() => {
          live.textContent = String(strings.added || '');
        });
      }

      window.clearTimeout(this.#addedTimer);
      this.#addedTimer = window.setTimeout(() => {
        // A choice made meanwhile ended "Added": the shopper stays (the add is sent when the pop-up closes).
        if (!this.#added) return;
        const component = /** @type {any} */ (this.closest('dialog-component'));
        if (this.closest('dialog')?.open && typeof component?.closeDialog === 'function') component.closeDialog();
        else this.#flush();
      }, ADDED_FOR);
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');
    }
  }

  /** "Order now, arrives {date}": the cart page's arrival date (its rule, settings and format). */
  async #showEta() {
    const eta = this.querySelector('[data-fd-qa-eta][data-days]');
    if (!(eta instanceof HTMLElement)) return;
    /** @type {string} */
    let text;
    try {
      const { arrivalDate } = await import('@theme/pdp-delivery');
      text = arrivalDate(eta.dataset);
    } catch (error) {
      // Only the date stays hidden.
      return;
    }
    for (const date of this.querySelectorAll('[data-fd-qa-eta-date]')) date.textContent = text;
    for (const line of this.querySelectorAll('[data-fd-qa-eta]')) {
      if (line instanceof HTMLElement) line.hidden = false;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // The size guide's dialog sits inside the pop-up's: its ×, its backdrop and its content are its own clicks, not a
    // click outside the pop-up (assets/dialog.js would close the pop-up too).
    if (target.closest('dialog.pdp-size-guide')) {
      event.stopPropagation();
      return;
    }

    const nav = target.closest('button.fd-qa__nav[data-dir]');
    if (nav instanceof HTMLElement && this.contains(nav)) {
      this.showImage(this.index + Number(nav.dataset.dir));
      return;
    }

    const thumb = target.closest('.fd-qa__thumbs button[data-index]');
    if (thumb instanceof HTMLElement && this.contains(thumb)) {
      this.showImage(Number(thumb.dataset.index));
      return;
    }

    const step = target.closest('button.fd-qa__step[data-step]');
    if (step instanceof HTMLElement && this.contains(step)) {
      this.#step(Number(step.dataset.step) || 0);
      return;
    }

    if (target.closest('button.fd-qa__add')) this.#add();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.matches('input.fd-qa__qty')) {
      input.value = String(this.quantity);
      this.#message = '';
      this.#render();
      return;
    }

    if (!input.matches('input.fd-qa__radio') || !input.checked) return;
    const group = input.closest('fieldset.fd-qa__group[data-option-index]');
    if (!(group instanceof HTMLElement)) return;
    this.selected[Number(group.dataset.optionIndex)] = input.value;
    if (!this.selected.some((value) => value === null)) this.#tried = false;
    this.#changed();
    if (group.dataset.kind === 'color') {
      const image = this.#colourImage();
      if (image !== null) this.showImage(image);
    }
    this.#syncQuantity();
    this.#render();
  };

  /** The total follows the quantity as it is typed. @param {Event} event */
  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('input.fd-qa__qty')) return;
    if (Number(input.value) >= 1) {
      this.#message = '';
      this.#render();
    }
  };

  /**
   * Esc in the size guide closes the size guide alone (the browser's own close request), not the pop-up as well.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('dialog.pdp-size-guide[open]')) event.stopPropagation();
  };
}

if (!customElements.get('fd-quick-add')) customElements.define('fd-quick-add', FdQuickAdd);
